import {
  directoryRelativeUrl,
  ensureTrailingSlash,
  isInDirectory,
  rootName,
} from "@tsmono/util";

import type { Log } from "../client/api/types";
import { scopePrefix, type OpenDatabase } from "../client/database";
import {
  pageRows,
  type DatabaseListingPlan,
  type DatabaseListingResult,
} from "../client/database/listing";

import { currentDatabase } from "./databaseInstance";
import { computeLogsWithRetried, type LogListingRow } from "./logListing";
import { getLogRows, isCacheOnlyListingScope } from "./logsContent";

/**
 * Where listing queries for `logDir` read their rows — an explicit,
 * scope-level decision rather than a per-query fallback:
 *
 * - a database handle: the normal dir-mode path; IndexedDB holds the
 *   replicated rows and is the row source.
 * - null: the react-query logs cache is the row source. This serves the
 *   out-of-namespace degrade (listing persistence skipped — see
 *   `namesInScope` in logsContent) and db-less sessions (the database
 *   failed to open; single-file mode renders no log list at all).
 */
const logsListingDatabase = (logDir: string): OpenDatabase | null =>
  isCacheOnlyListingScope(logDir) ? null : currentDatabase();

const scanRows = async (logDir: string, prefix: string): Promise<Log[]> => {
  const db = logsListingDatabase(logDir);
  if (db) {
    const logs = await db.readLogs({ prefix });
    if (logs !== null) return logs;
    // A failed db read degrades to the cache for this query only.
  }
  // An out-of-namespace scope's names never start with the scope prefix —
  // that mismatch is what degraded it (see `namesInScope`) — so filtering
  // would drop every row. Serve the whole listing; `toRow` owns membership.
  if (isCacheOnlyListingScope(logDir)) return getLogRows(logDir);
  const scope = scopePrefix(prefix);
  return getLogRows(logDir).filter((row) => row.name.startsWith(scope));
};

/**
 * Run a listing plan over `logDir`'s rows: scan the source, mark retried
 * runs (a cross-row derivation, so it runs over the scan, before `toRow`),
 * shape each record through `toRow` (which owns row-universe membership —
 * it drops records the view has no row for), then filter, sort, paginate.
 *
 * Deliberately NOT gated on the scope's sync state: results reflect
 * whatever has replicated so far — a warm cache from a prior session, or a
 * partially-landed sync — and the write path's invalidation refreshes
 * observers as further writes land. Callers surface sync progress
 * separately rather than hiding rows behind it.
 *
 * `prefix` narrows the scan (folder mode lists a subdirectory). Retried
 * grouping keys on a row's exact parent directory, so a boundary-safe
 * prefix scan never splits a group and the marking matches a whole-dir
 * scan's.
 */
const scanListingRows = async <TRow>(
  logDir: string,
  prefix: string,
  toRow: (log: LogListingRow) => TRow | undefined,
  plan: DatabaseListingPlan<TRow>
): Promise<TRow[]> => {
  const scanned = await scanRows(logDir, prefix);
  const rows: TRow[] = [];
  for (const log of computeLogsWithRetried(scanned)) {
    const row = toRow(log);
    if (row !== undefined && plan.matches(row)) rows.push(row);
  }
  // Stable sort over the scan's listing order (mtime-descending), so ties —
  // and the unsorted listing — keep that order without a position tiebreak.
  if (plan.compare) rows.sort(plan.compare);
  return rows;
};

export const readLogsListing = async <TRow>(
  logDir: string,
  prefix: string,
  toRow: (log: LogListingRow) => TRow | undefined,
  plan: DatabaseListingPlan<TRow>
): Promise<DatabaseListingResult<TRow>> => {
  const rows = await scanListingRows(logDir, prefix, toRow, plan);
  const total_count = rows.length;
  return { ...pageRows(rows, plan.pagination), total_count };
};

/** View inputs for {@link readLogsOverview}. */
export interface LogsOverviewView {
  /** Folder-mode current directory; unset in the flat tasks view (which
   *  lists the whole dir and derives no folders). */
  folderDir?: string;
  showRetriedLogs: boolean;
  /** Pre-hide row-universe membership for the view (`fileLogIdentity`
   *  presence — path logic only; the overview applies retried-hiding
   *  itself so it can also count what hiding removed). */
  isCandidate: (log: LogListingRow) => boolean;
}

/** Aggregate facts about a scope that the log-list page needs beyond the
 *  queried rows themselves. See {@link readLogsOverview}. */
export interface LogsOverview {
  /** Distinct task_ids with a log anywhere under the dir — the pending-task
   *  anti-join input. */
  taskIds: string[];
  /** File rows in the view universe (retried-hidden excluded). */
  fileCount: number;
  /** Among `fileCount`, logs still running (status "started"). */
  startedCount: number;
  /** Retried runs in the view universe pre-hiding — drives the
   *  "Show Retried Logs" toggle's visibility. */
  retriedCount: number;
  /** Set when exactly one file row exists (single-log workspace redirect). */
  soleFileName: string | undefined;
  /** Folder-mode: the current directory's immediate subdirectories. */
  folders: { name: string; itemCount: number }[];
}

/** Immediate subdirectories of `currentDir` with per-folder log counts. */
const deriveFolders = (
  rows: LogListingRow[],
  currentDir: string
): { name: string; itemCount: number }[] => {
  const dirWithSlash = ensureTrailingSlash(currentDir);

  // Count logs under a path prefix via binary search rather than a full
  // scan per folder. Names sort into contiguous ranges, so a prefix count
  // is two bound lookups.
  const sortedNames = rows.map((row) => row.name).sort();
  const lowerBound = (target: string): number => {
    let lo = 0;
    let hi = sortedNames.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const name = sortedNames[mid];
      if (name !== undefined && name < target) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };
  const countWithPrefix = (prefix: string): number =>
    lowerBound(prefix + "￿") - lowerBound(prefix);

  const folders: { name: string; itemCount: number }[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const name = row.name;
    if (isInDirectory(name, currentDir) || !name.startsWith(dirWithSlash)) {
      continue;
    }
    const relativePath = directoryRelativeUrl(name, currentDir);
    // encodeURIComponent/decodeURIComponent round-trip, so this is the raw
    // first path segment under `currentDir` — the folder's own directory.
    const dirName = decodeURIComponent(rootName(relativePath));
    if (seen.has(dirName)) continue;
    seen.add(dirName);
    // Count under the folder's path, slash-terminated: an unterminated
    // prefix would also span sibling folders sharing the name as a prefix
    // (sub vs sub2), and the first-seen file's parent dir would miss logs
    // outside its own subtree when that file is nested deeper.
    folders.push({
      name: dirName,
      itemCount: countWithPrefix(dirWithSlash + dirName + "/"),
    });
  }
  return folders;
};

/**
 * One scan of `logDir`'s rows producing the page-level aggregates: pending
 * anti-join input, progress/footer counts, retried presence, the sole-file
 * redirect target, and folder summaries. These are the derivations that
 * would otherwise force the full row list into memory beside the row query;
 * keeping them behind one read means pagination only changes this module.
 * Like `readLogsListing`, deliberately not gated on sync state.
 */
export const readLogsOverview = async (
  logDir: string,
  view: LogsOverviewView
): Promise<LogsOverview> => {
  const scanned = await scanRows(logDir, logDir);
  const rows = computeLogsWithRetried(scanned);

  const taskIds = new Set<string>();
  let fileCount = 0;
  let startedCount = 0;
  let retriedCount = 0;
  let soleFileName: string | undefined;
  for (const log of rows) {
    if (log.task_id) taskIds.add(log.task_id);
    if (!view.isCandidate(log)) continue;
    if (log.retried) {
      retriedCount += 1;
      if (!view.showRetriedLogs) continue;
    }
    fileCount += 1;
    soleFileName = fileCount === 1 ? log.name : undefined;
    if (log.status === "started") startedCount += 1;
  }

  return {
    taskIds: [...taskIds],
    fileCount,
    startedCount,
    retriedCount,
    soleFileName,
    folders:
      view.folderDir === undefined ? [] : deriveFolders(rows, view.folderDir),
  };
};

/**
 * Ids of the rows whose searchable text contains `term`
 * (case-insensitive), in listing order under the same universe + plan as
 * the row query — the find band's data-level backing. Runs over the scan
 * today; under keys-first pagination it becomes a snapshot projection, so
 * matches keep covering rows outside the loaded pages.
 */
export const readLogsListingMatches = async <TRow>(
  logDir: string,
  prefix: string,
  toRow: (log: LogListingRow) => TRow | undefined,
  plan: DatabaseListingPlan<TRow>,
  find: {
    term: string;
    getRowId: (row: TRow) => string;
    /** A row's searchable text, already lowercased (`rowSearchText`'s
     *  contract) — the scan must not pay a second per-row lowering. */
    rowText: (row: TRow) => string;
  }
): Promise<string[]> => {
  const rows = await scanListingRows(logDir, prefix, toRow, plan);
  const term = find.term.toLowerCase();
  const ids: string[] = [];
  for (const row of rows) {
    if (find.rowText(row).includes(term)) {
      ids.push(find.getRowId(row));
    }
  }
  return ids;
};
